import React, { useEffect, useState } from "react";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { LEAVE_ACTION } from "../../redux/action";
import API from "../../utils/api";
import { Toaster, dateFormatter } from "../../utils/common";
import { hasHrmsAdminAccess } from "../../utils/hrmsAccess";
import { isTeamFirstApprover } from "../../utils/leaveTeamApprovers";
import "../../components/layout/logoutModal.css";

const BALANCE_TYPES = ["Casual", "Sick", "Comp Off"];

const sameUserId = (left, right) =>
  String(left || "").trim().toLowerCase() ===
  String(right || "").trim().toLowerCase();

const hasApprovalId = (value) => String(value || "").trim() !== "";

function viewerEmail() {
  try {
    const stored = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
    return stored?.email || stored?.user_email || "";
  } catch (_error) {
    return "";
  }
}

function leaveStatusText(leave) {
  const status = String(leave?.status ?? "");
  if (status === "2") return "Approved";
  if (status === "3") return "Rejected";
  if (status === "0") return "Draft";
  const managerDone = hasApprovalId(leave?.manager_approved_by);
  const adminDone = hasApprovalId(leave?.admin_approved_by);
  if (managerDone && !adminDone) return "1 of 2 - Manager approved, HR pending";
  if (adminDone && !managerDone) return "1 of 2 - HR approved, manager pending";
  return "0 of 2 - Manager and HR pending";
}

function leaveTypeText(leave) {
  const name = String(leave?.leave_type || "Leave");
  const emergency =
    leave?.is_emergency === true ||
    leave?.is_emergency === 1 ||
    String(leave?.is_emergency || "") === "1";
  return emergency ? `${name} (Emergency)` : name;
}

function canActOnLeave(leave, userId, userEmail) {
  const pending = String(leave?.status) === "1";
  const notSelf = !sameUserId(leave?.employee_id, userId);
  const isAdmin = hasHrmsAdminAccess(userId);
  const teamApprover =
    !isAdmin && isTeamFirstApprover(userEmail, leave?.employee_team);
  const isManagerOfApplicant = sameUserId(leave?.manager_id, userId);
  const canApprove =
    pending &&
    notSelf &&
    ((!hasApprovalId(leave?.manager_approved_by) &&
      (isManagerOfApplicant || teamApprover)) ||
      (isAdmin &&
        hasApprovalId(leave?.manager_approved_by) &&
        !hasApprovalId(leave?.admin_approved_by) &&
        !sameUserId(leave?.manager_approved_by, userId)));
  const canReject =
    pending &&
    notSelf &&
    (isAdmin || isManagerOfApplicant || teamApprover);
  return { canApprove, canReject };
}

const LeaveNotificationPopup = ({ leaveId, onClose }) => {
  const userId = localStorage.getItem("user_id");
  const userEmail = viewerEmail();
  const [leave, setLeave] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectionComments, setRejectionComments] = useState("");

  const loadLeave = async (id) => {
    const details = await LEAVE_ACTION.getLeaveDetails(id);
    if (!details || typeof details !== "object" || !details.leave_id) {
      throw new Error(
        typeof details === "string" ? details : "Could not open this leave"
      );
    }
    let nextBalances = [];
    if (details.employee_id) {
      const balanceRes = await API.get("leave/getLeaveBalance", {
        params: { employee_id: details.employee_id },
      });
      const rows = balanceRes?.data?.data;
      nextBalances = Array.isArray(rows)
        ? rows.filter((row) => BALANCE_TYPES.includes(row.leave_type))
        : [];
    }
    setLeave(details);
    setBalances(
      BALANCE_TYPES.map(
        (type) =>
          nextBalances.find((row) => row.leave_type === type) || {
            leave_type: type,
            total_leaves: 0,
            used_leaves: 0,
            balance: 0,
          }
      )
    );
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loadLeave(leaveId);
      } catch (error) {
        if (!cancelled) {
          Toaster.error(error?.message || "Could not open this leave");
          onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leaveId]);

  const { canApprove, canReject } = leave
    ? canActOnLeave(leave, userId, userEmail)
    : { canApprove: false, canReject: false };

  const handleApprove = async () => {
    if (!leave?.leave_id) return;
    setSaving(true);
    try {
      const response = await LEAVE_ACTION.approveLeave(leave.leave_id);
      if (response?.status) {
        Toaster.success(response.message || "Leave approved successfully");
        await loadLeave(leave.leave_id);
      } else {
        Toaster.error(response?.message || "Error approving leave");
      }
    } catch (error) {
      Toaster.error("Error approving leave");
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!leave?.leave_id) return;
    if (!rejectionComments.trim()) {
      Toaster.error("Please provide rejection reason");
      return;
    }
    setSaving(true);
    try {
      const response = await LEAVE_ACTION.rejectLeave({
        leave_id: leave.leave_id,
        rejected_comments: rejectionComments.trim(),
      });
      if (response?.status) {
        Toaster.success(response.message || "Leave rejected successfully");
        setRejecting(false);
        setRejectionComments("");
        await loadLeave(leave.leave_id);
      } else {
        Toaster.error(response?.message || "Error rejecting leave");
      }
    } catch (error) {
      Toaster.error("Error rejecting leave");
    } finally {
      setSaving(false);
    }
  };

  const dayType =
    String(leave?.day_type || "").toLowerCase() === "half"
      ? "Half day"
      : "Full day";

  return (
    <div className="logout-modal-overlay" onClick={onClose}>
      <div
        className="logout-modal leave-notification-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "16px",
          }}
        >
          <h2 className="logout-modal-title" style={{ margin: 0 }}>
            Leave details
          </h2>
          <IconButton
            onClick={onClose}
            aria-label="close"
            style={{ padding: "8px" }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        {loading || !leave ? (
          <p className="logout-modal-question">Loading leave details...</p>
        ) : (
          <>
            <div className="leave-notification-details">
              <span>Employee</span>
              <span>{leave.employee_name || "N/A"}</span>
              <span>Leave type</span>
              <span>{leaveTypeText(leave)}</span>
              <span>Day type</span>
              <span>{dayType}</span>
              <span>Start date</span>
              <span>{dateFormatter(leave.start_date) || "N/A"}</span>
              <span>End date</span>
              <span>{dateFormatter(leave.end_date) || "N/A"}</span>
              <span>Days</span>
              <span>{leave.total_days ?? "N/A"}</span>
              <span>Status</span>
              <span>{leaveStatusText(leave)}</span>
              <span>Reason</span>
              <span>{leave.reason || "—"}</span>
            </div>

            <h3 className="leave-notification-balance-title">
              Available leaves
            </h3>
            <div className="leave-notification-balances">
              {balances.map((row) => (
                <div key={row.leave_type} className="leave-notification-balance">
                  <strong>{row.leave_type}</strong>
                  <span>Total: {row.total_leaves ?? 0}</span>
                  <span>Used: {row.used_leaves ?? 0}</span>
                  <span>Available: {row.balance ?? 0}</span>
                </div>
              ))}
            </div>

            {rejecting ? (
              <div className="form-row">
                <textarea
                  className="leave-notification-comments"
                  value={rejectionComments}
                  onChange={(event) => setRejectionComments(event.target.value)}
                  placeholder="Enter reason for rejection..."
                  rows={3}
                />
              </div>
            ) : null}
          </>
        )}

        <div className="logout-modal-buttons">
          {rejecting ? (
            <>
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-no"
                onClick={() => {
                  setRejecting(false);
                  setRejectionComments("");
                }}
                disabled={saving}
              >
                No
              </button>
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-yes"
                onClick={handleReject}
                disabled={saving}
                style={{ backgroundColor: "#f44336" }}
              >
                Yes
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="logout-modal-btn logout-modal-btn-no"
                onClick={onClose}
                disabled={saving}
              >
                Close
              </button>
              {canReject ? (
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-yes"
                  onClick={() => setRejecting(true)}
                  disabled={saving || loading}
                  style={{ backgroundColor: "#f44336" }}
                >
                  Reject
                </button>
              ) : null}
              {canApprove ? (
                <button
                  type="button"
                  className="logout-modal-btn logout-modal-btn-yes"
                  onClick={handleApprove}
                  disabled={saving || loading}
                  style={{ backgroundColor: "var(--Primary-color)" }}
                >
                  Approve
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveNotificationPopup;
